import { GetHomePageDataQuery } from 'generated/graphql';
import { PATHS } from '@client/routes';
import auth from '@client/components/auth';
import { Collections } from './home.domain';
import { HomeVMVDispatcher } from './home.viewModel';
import { faMobile } from '@fortawesome/pro-regular-svg-icons';
import { faRocket } from '@fortawesome/pro-solid-svg-icons';
import { ButtonColor } from '@design-system/components/button/button.css';
import { toCollectionLink } from '../../collections/collection-links';
import { Link } from 'react-router';

import community from './components/community.png';
import coaching from './components/coaching.png';
import studio from './components/studio.png';

export type Feedback = Array<{
  id: number;
  name: string;
  thumbnail: string;
  rating: number;
  workoutName: string;
  text: string;
  createdAt: number;
  userId?: number;
}>;

export type CreatorLinksType = Array<{
  title: string;
  url: string;
  description: string;
}>;

export const toFeedbackData = (
  data: GetHomePageDataQuery['searchComments'],
): Feedback => {
  return data.map((comment) => ({
    id: comment.id,
    name: comment.user?.name || '',
    thumbnail:
      `${comment.user?.mediaThumbnail?.cdn01}${comment.user?.mediaThumbnail?.image}` ||
      '',
    rating: comment.userActivity?.floatValue || 0,
    workoutName: comment.workout?.name || '',
    text: comment.textValue,
    createdAt: Number(comment.createdAt),
    userId: comment.user?.id,
  }));
};

export const getChecklistItems = ({
  data,
  creatorId,
  email,
  isLive,
  dispatch,
}: {
  data?: {
    profileReady: boolean;
    paymentMethodReady: boolean;
    collectionPublishedReady: boolean;
  };
  creatorId?: number;
  email?: string;
  isLive: boolean;
  dispatch: HomeVMVDispatcher;
}) => {
  if (!data || !creatorId || isLive) return [];
  const { profileReady, paymentMethodReady, collectionPublishedReady } = data;

  return [
    {
      title: 'Complete profile',
      done: profileReady,
      to: getEditProfileLink(creatorId, email),
    },
    {
      title: 'Set up Stripe',
      done: paymentMethodReady,
      to: getPaymentLink(creatorId),
    },
    {
      title: 'Setup offer',
      done: collectionPublishedReady,
      onClick: () => dispatch('setupOffer'),
    },
  ];
};

export const getEditProfileLink = (creatorId: number, email?: string) => {
  if (!email) {
    return auth.isInfluencer()
      ? PATHS.CREATOR.SETTINGS
      : PATHS.PARTNER_SETTINGS(creatorId);
  }
  return auth.isInfluencer()
    ? PATHS.CREATOR.PROFILE(true)
    : PATHS.PARTNER_EDIT(creatorId);
};

const getPaymentLink = (creatorId: number) => {
  return auth.isInfluencer()
    ? PATHS.ADMIN_PAYMENT(creatorId)
    : PATHS.ADMIN_ADMINISTRATION(creatorId);
};

export const getLinks = (
  linksType: 'otp' | 'subscription' | 'both',
  isCreatorExclusive: boolean,
  customURL?: string,
  profileDeepLink?: string,
) => {
  const landingPageLink = customURL
    ? `${import.meta.env.VITE_LANDING_PAGE_URL}/${customURL}`
    : '';

  const exclusiveCreatorSubscriptionLinks = [
    toCollectionLink('channelLandingPage', landingPageLink),
    toCollectionLink(
      'subscriptionCheckoutPage',
      landingPageLink ? `${landingPageLink}/checkout` : '',
    ),
    toCollectionLink('channelDeepLink', profileDeepLink),
  ];
  const platformCreatorSubscriptionLinks = [
    ...exclusiveCreatorSubscriptionLinks,
    toCollectionLink(
      'giftingPage',
      landingPageLink ? `${landingPageLink}/gift` : '',
    ),
  ];

  const subscriptionLinks = isCreatorExclusive
    ? exclusiveCreatorSubscriptionLinks
    : platformCreatorSubscriptionLinks;
  const otpLinks = [toCollectionLink('channelDeepLink', profileDeepLink)];

  const links = linksType === 'otp' ? otpLinks : subscriptionLinks;

  return links.filter((link) => link !== undefined);
};

export const getHomeCta = ({
  isLive,
  dispatch,
  canGoLive,
}: {
  isLive: boolean;
  dispatch: HomeVMVDispatcher;
  canGoLive: boolean;
}) => {
  if (isLive) return;

  return [
    {
      icon: faRocket,
      content: 'Go live',
      onClick: () => dispatch('openGoLiveModal'),
      disabled: !canGoLive,
      color: 'success' as ButtonColor,
    },
    {
      icon: faMobile,
      onClick: () => dispatch('openQrCodeModal'),
    },
  ];
};

type RawCollections = GetHomePageDataQuery['collections'];

export const getOTPPrograms = (collections: RawCollections): Collections => {
  if (!collections) return [];

  return collections
    .filter(
      (i) =>
        i.accessType === 'exclusive' &&
        i.publishedAt !== null &&
        i.archivedAt === null &&
        i.name !== null,
    )
    .map((i) => ({
      ...i,
      name: i.name || 'not found',
      exclusiveProgramMetadata: i.exclusiveProgramMetadata || null,
      publishedAt: i.publishedAt || null,
      archivedAt: i.archivedAt || null,
    }));
};

export const getSubscriptionPrograms = (
  collections: RawCollections,
): Collections => {
  if (!collections) return [];

  return collections
    .filter(
      (i) =>
        i.accessType === 'premium' &&
        i.publishedAt !== null &&
        i.archivedAt === null &&
        i.name !== null,
    )
    .map((i) => ({
      ...i,
      name: i.name || 'not found',
      exclusiveProgramMetadata: i.exclusiveProgramMetadata || null,
      publishedAt: i.publishedAt || null,
      archivedAt: i.archivedAt || null,
    }));
};

export const getAfterDesc = (
  linksType: 'otp' | 'subscription' | 'both',
  influencerId: number,
): React.ReactNode => {
  if (linksType === 'otp')
    return (
      <>
        For direct links to individual programs, go to the{' '}
        <Link to={PATHS.PURCHASES(influencerId)}>One -Time Purchases tab</Link>
      </>
    );
  if (linksType === 'subscription')
    return (
      <>
        For direct links to individual programs, go to the{' '}
        <Link to={PATHS.SUBSCRIPTIONS(influencerId)}>Subscription tab</Link>
      </>
    );
  if (linksType === 'both')
    return (
      <>
        For direct links to individual programs, go to the{' '}
        <Link to={PATHS.PURCHASES(influencerId)}>One -Time Purchases tab</Link>{' '}
        or <Link to={PATHS.SUBSCRIPTIONS(influencerId)}>Subscription tab</Link>
      </>
    );
};

export const externalUrls = {
  handbook: 'http://www.joinplaybook.com/handbook',
  community: 'https://community.joinplaybook.com/',
  coaching: 'https://www.joinplaybook.com/get-coaching',
  studio: 'https://www.playbookstudio.co',
  webinar:
    'https://community.joinplaybook.com/c/office-hours/quickstart-webinar',
};

export const cardsSection = [
  {
    title: 'Creator Community',
    description:
      'Join our FREE community with weekly tips and tricks to help you dominate in the space ',
    image: community,
    link: externalUrls['community'],
  },
  {
    title: 'Business Coaching',
    description:
      'Get coaching to grow your socials and set your business up for success',
    image: coaching,
    link: externalUrls['coaching'],
  },
  {
    title: 'Playbook Studio',
    description:
      'Check out our studio that offers design services, program building support and much more! ',
    image: studio,
    link: externalUrls['studio'],
  },
];
